import SessionStore from 'stores/SessionStore';
import { getRequestHeaders } from '../helpers/auth_helpers';
export function initializeOAuth() {
    const user = SessionStore.getUser();
    const url = `${AI_ASSISTANT_SERVICE}/gmail/auth?` +
        new URLSearchParams({ id: user._id }).toString();
    // Open a real popup handle (no noopener/noreferrer in features)
    const popup = window.open('', '_blank', 'width=500,height=700');
    if (!popup) {
        // Popup truly blocked (user setting). Offer a manual link or same-tab nav.
        // window.location.assign(url);
        return { windowRef: null, blocked: true };
    }
    try {
        popup.opener = null;
    }
    catch (e) {
        // Prevent reverse tabnabbing (equivalent to rel="noopener")
    }
    // Navigate popup on a micro-delay (helps Chrome/Safari)
    setTimeout(() => {
        var _a;
        if (!popup.closed) {
            try {
                popup.location.replace(url); // avoids back button clutter
            }
            catch (e) {
                console.error(e);
                popup.location.href = url;
            }
            (_a = popup.focus) === null || _a === void 0 ? void 0 : _a.call(popup);
        }
    }, 0);
    // Optional: delayed fallback ONLY if popup gets closed or navigation failed
    setTimeout(() => {
        if (popup.closed) {
            // window.location.assign(url); // enable if you want same-tab fallback
        }
    }, 800);
    return { windowRef: popup };
}
// Helper function to safely extract content from AI assistant result
function extractAIContent(result) {
    if (typeof result === 'string') {
        return result;
    }
    if (typeof result === 'object' && result !== null) {
        // Check if the object has content or message properties
        if ('content' in result && typeof result.content === 'string') {
            return result.content;
        }
        if ('message' in result && typeof result.message === 'string') {
            return result.message;
        }
    }
    return '';
}
// Type guard to validate MCP response structure
function isValidMcpResponse(obj) {
    return (typeof obj === 'object' &&
        obj !== null &&
        'jsonrpc' in obj &&
        obj.jsonrpc === '2.0' &&
        'id' in obj &&
        typeof obj.id === 'number');
}
// Store MCP sessions by conversation ID
const mcpSessions = new Map(); // conversationId -> sessionId
/**
 * Parse Server-Sent Events (SSE) response format
 * @param responseText - The raw response text
 * @returns Parsed JSON object from the data field
 */
function parseSSEResponse(responseText) {
    const lines = responseText.trim().split('\n');
    let data = null;
    for (const line of lines) {
        if (line.startsWith('data: ')) {
            const jsonStr = line.substring(6); // Remove 'data: ' prefix
            if (jsonStr.trim()) {
                data = JSON.parse(jsonStr);
                break;
            }
        }
    }
    if (!data) {
        throw new Error('No valid data field found in SSE response');
    }
    return data;
}
/**
 * Convert file to base64 string
 * @param file - The file to convert
 * @returns Base64 string representation of the file
 */
function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result;
            if (typeof result === 'string') {
                // Remove the data URL prefix (e.g., "data:image/png;base64,")
                const base64 = result.split(',')[1];
                resolve(base64);
            }
            else {
                reject(new Error('FileReader result is not a string'));
            }
        };
        reader.onerror = (error) => reject(error);
    });
}
/**
 * Initialize MCP session and return session object
 * @param token - Authorization token
 * @returns Session object with id
 */
export async function initializeMcpSession(token = null) {
    // If token is provided directly, use it; otherwise get from headers
    const authToken = token || getRequestHeaders().Authorization || '';
    const initializePayload = {
        jsonrpc: '2.0',
        id: 1,
        method: 'initialize',
        params: {
            protocolVersion: '2024-11-05',
            capabilities: {
                tools: {},
            },
            clientInfo: {
                name: 'cargomatic-command',
                version: '1.0.0',
            },
        },
    };
    const res = await fetch(`${AI_ASSISTANT_SERVICE}/mcp`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/event-stream',
            Authorization: authToken,
        },
        body: JSON.stringify(initializePayload),
    });
    if (!res.ok) {
        const err = await res.text();
        throw new Error(`Failed to initialize MCP session: ${res.status} ${res.statusText} — ${err}`);
    }
    // Extract session ID from response headers - try different case variations
    let sessionId = res.headers.get('Mcp-Session-Id');
    if (!sessionId) {
        sessionId = res.headers.get('mcp-session-id');
    }
    if (!sessionId) {
        sessionId = res.headers.get('MCP-SESSION-ID');
    }
    if (!sessionId) {
        throw new Error('No session ID received from MCP server');
    }
    return {
        id: sessionId,
    };
}
/**
 * Get or create MCP session for a conversation
 * @param conversationId - The conversation ID
 * @param token - Optional authorization token
 * @returns Session ID for the conversation
 */
export async function getMcpSessionForConversation(conversationId, token = null) {
    if (!conversationId) {
        throw new Error('Conversation ID is required');
    }
    // Check if we already have a session for this conversation
    let sessionId = mcpSessions.get(conversationId);
    if (!sessionId) {
        // Create new session for this conversation
        const session = await initializeMcpSession(token);
        sessionId = session.id;
        mcpSessions.set(conversationId, sessionId);
    }
    return sessionId;
}
/**
 * Handle session errors and retry with new session if needed
 * @param error - The error response from MCP
 * @param conversationId - The conversation ID
 * @param token - Optional authorization token
 * @returns New session ID if session was refreshed, null otherwise
 */
async function handleSessionError(error, conversationId, token = null) {
    var _a;
    // Check if this is an invalid session error
    if ((error === null || error === void 0 ? void 0 : error.code) === -32000 && ((_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.includes('Invalid session'))) {
        // Clear the invalid session and create a new one
        mcpSessions.delete(conversationId);
        try {
            const session = await initializeMcpSession(token);
            mcpSessions.set(conversationId, session.id);
            return session.id;
        }
        catch (_initError) {
            console.error(_initError);
            return null;
        }
    }
    return null;
}
/**
 * Call MCP tool with session ID and automatic session refresh on invalid session
 * @param sessionId - MCP session ID
 * @param toolName - Name of the tool to call
 * @param toolArgs - Tool arguments
 * @param token - Optional authorization token
 * @param conversationId - Optional conversation ID for session refresh
 * @returns Tool response with result or error
 */
export async function callMcpTool(sessionId, toolName, toolArgs = {}, token = null, conversationId = null) {
    // If token is provided directly, use it; otherwise get from headers
    const authToken = token || getRequestHeaders().Authorization || '';
    const toolCallPayload = {
        jsonrpc: '2.0',
        id: 2,
        method: 'tools/call',
        params: {
            name: toolName,
            arguments: toolArgs,
        },
    };
    console.log('Calling MCP tool:', {
        sessionId,
        toolName,
        toolArgs,
        payload: toolCallPayload,
    });
    const res = await fetch(`${AI_ASSISTANT_SERVICE}/mcp`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/event-stream',
            Authorization: authToken,
            'mcp-session-id': sessionId,
        },
        body: JSON.stringify(toolCallPayload),
    });
    console.log('MCP tool response status:', res.status, res.statusText);
    if (!res.ok) {
        const err = await res.text();
        console.error('MCP tool call failed:', err);
        throw new Error(`Failed to call MCP tool: ${res.status} ${res.statusText} — ${err}`);
    }
    const responseText = await res.text();
    console.log('MCP tool response text:', responseText);
    // Parse SSE response if it's in that format
    let responseJson;
    try {
        // First try to parse as regular JSON
        const parsed = JSON.parse(responseText);
        // Validate the parsed response has the expected structure
        if (isValidMcpResponse(parsed)) {
            responseJson = parsed;
        }
        else {
            throw new Error('Invalid MCP response format');
        }
    }
    catch (parseError) {
        // If that fails, try to parse as SSE format
        try {
            const parsed = parseSSEResponse(responseText);
            // Validate the parsed response has the expected structure
            if (isValidMcpResponse(parsed)) {
                responseJson = parsed;
            }
            else {
                throw new Error('Invalid MCP response format');
            }
        }
        catch (sseParseError) {
            console.error('Failed to parse MCP response as JSON or SSE:', parseError, 'SSE parse error:', sseParseError, 'Response text:', responseText);
            return {
                error: { message: 'Invalid JSON or SSE response', code: -32700 },
                result: null,
            };
        }
    }
    console.log('MCP tool response JSON:', responseJson);
    if (responseJson.error) {
        console.error('MCP tool error:', responseJson.error);
        // Check if this is an invalid session error and we can retry
        if (conversationId) {
            const newSessionId = await handleSessionError(responseJson.error, conversationId, token);
            if (newSessionId) {
                console.log('Retrying with new session ID:', newSessionId);
                // Retry the call with the new session ID
                return await callMcpTool(newSessionId, toolName, toolArgs, token, conversationId);
            }
        }
        return {
            error: responseJson.error,
            result: null,
        };
    }
    else {
        console.log('MCP tool success, result:', responseJson.result);
        return {
            error: null,
            result: responseJson.result,
        };
    }
}
/**
 * Call MCP tool with file upload and automatic session refresh
 * @param sessionId - MCP session ID
 * @param toolName - Name of the tool to call
 * @param toolArgs - Tool arguments
 * @param files - Files to upload
 * @param token - Optional authorization token
 * @param conversationId - Optional conversation ID for session refresh
 * @returns Tool response with result or error
 */
export async function callMcpToolWithFiles(sessionId, toolName, toolArgs = {}, files, token = null, conversationId = null) {
    // If token is provided directly, use it; otherwise get from headers
    const authToken = token || getRequestHeaders().Authorization || '';
    // Convert files to base64 format for JSON payload
    const filesArray = [];
    if (Array.isArray(files)) {
        for (const file of files) {
            // If file already has base64 data, use it directly
            if (file.base64) {
                filesArray.push({
                    filename: file.name,
                    contentBase64: file.base64,
                });
            }
            else if (file instanceof File) {
                // Convert File object to base64
                const base64 = await fileToBase64(file);
                filesArray.push({
                    filename: file.name,
                    contentBase64: base64,
                });
            }
        }
    }
    else if (files instanceof FileList) {
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const base64 = await fileToBase64(file);
            filesArray.push({
                filename: file.name,
                contentBase64: base64,
            });
        }
    }
    else if (files instanceof File) {
        const base64 = await fileToBase64(files);
        filesArray.push({
            filename: files.name,
            contentBase64: base64,
        });
    }
    const toolCallPayload = {
        jsonrpc: '2.0',
        id: 3,
        method: 'tools/call',
        params: {
            name: toolName,
            arguments: Object.assign(Object.assign({}, toolArgs), { files: filesArray }),
        },
    };
    const res = await fetch(`${AI_ASSISTANT_SERVICE}/mcp`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/event-stream',
            Authorization: authToken,
            'mcp-session-id': sessionId,
        },
        body: JSON.stringify(toolCallPayload),
    });
    if (!res.ok) {
        const err = await res.text();
        throw new Error(`Failed to call MCP tool with files: ${res.status} ${res.statusText} — ${err}`);
    }
    const responseText = await res.text();
    // Parse SSE response if it's in that format
    let responseJson;
    try {
        // First try to parse as regular JSON
        const parsed = JSON.parse(responseText);
        // Validate the parsed response has the expected structure
        if (isValidMcpResponse(parsed)) {
            responseJson = parsed;
        }
        else {
            throw new Error('Invalid MCP response format');
        }
    }
    catch (parseError) {
        // If that fails, try to parse as SSE format
        try {
            const parsed = parseSSEResponse(responseText);
            // Validate the parsed response has the expected structure
            if (isValidMcpResponse(parsed)) {
                responseJson = parsed;
            }
            else {
                throw new Error('Invalid MCP response format');
            }
        }
        catch (sseParseError) {
            console.warn('Failed to parse MCP response as JSON or SSE:', parseError, 'SSE parse error:', sseParseError, 'Response text:', responseText);
            return {
                error: { message: 'Invalid JSON or SSE response', code: -32700 },
                result: null,
            };
        }
    }
    if (responseJson.error) {
        // Check if this is an invalid session error and we can retry
        if (conversationId) {
            const newSessionId = await handleSessionError(responseJson.error, conversationId, token);
            if (newSessionId) {
                // Retry the call with the new session ID
                return await callMcpToolWithFiles(newSessionId, toolName, toolArgs, files, token, conversationId);
            }
        }
        return {
            error: responseJson.error,
            result: null,
        };
    }
    else {
        return {
            error: null,
            result: responseJson.result,
        };
    }
}
/**
 * Legacy method for backward compatibility - Get AI assistant response
 * @param query - The user's query text
 * @param conversationId - The conversation ID
 * @param shipmentId - Optional shipment ID for context
 * @returns The AI assistant response
 */
export async function getAIAssistantResponse(query, conversationId, shipmentId = null) {
    if (!query || !query.trim()) {
        throw new Error('Query is required');
    }
    if (!conversationId) {
        throw new Error('Conversation ID is required');
    }
    // Get or create session for this conversation
    const sessionId = await getMcpSessionForConversation(conversationId);
    // Build tool arguments
    const toolArgs = {
        message: query,
        conversationId: conversationId,
    };
    // Add shipment context if available
    if (shipmentId) {
        toolArgs.shipmentId = shipmentId;
    }
    // Use the MCP-based Friday invocation with conversation-specific session and auto-retry
    const result = await callMcpTool(sessionId, 'friday', toolArgs, null, conversationId); // Pass conversationId for auto-retry
    if (result.error) {
        throw new Error(`MCP Error: ${result.error.message || 'Unknown error'}`);
    }
    // Return in the expected format for backward compatibility
    return {
        response: extractAIContent(result.result),
        data: result.result,
    };
}
/**
 * Get the session ID for a specific conversation
 * @param conversationId - The conversation ID
 * @returns The session ID for the conversation
 */
export function getMcpSessionId(conversationId = null) {
    if (conversationId) {
        return mcpSessions.get(conversationId) || null;
    }
    // If no conversationId provided, return the first session (for backward compatibility)
    const sessions = Array.from(mcpSessions.values());
    return sessions.length > 0 ? sessions[0] : null;
}
/**
 * Clear the MCP session for a specific conversation
 * @param conversationId - The conversation ID to clear
 */
export function clearMcpSession(conversationId = null) {
    if (conversationId) {
        const sessionId = mcpSessions.get(conversationId);
        if (sessionId) {
            mcpSessions.delete(conversationId);
            console.log(`Cleared MCP session ${sessionId} for conversation ${conversationId}`);
        }
    }
    else {
        // Clear all sessions if no specific conversation ID
        mcpSessions.clear();
        console.log('Cleared all MCP sessions');
    }
}
/**
 * Get all active MCP sessions
 * @returns Map of conversationId to sessionId
 */
export function getAllMcpSessions() {
    return new Map(mcpSessions);
}
